// Enhanced ProjectsScreen.js - Integrating existing project management functionality
// This combines our modern B2B portal design with rich project management features

import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  Alert,
  RefreshControl,
  Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

import { COLORS, SIZES, FONTS } from '../../../constants/theme';

const { width } = Dimensions.get('window');

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  projects: `${API_BASE_URL}/api/projects`,
  companyProfile: `${API_BASE_URL}/api/company/profile`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id')
                      || await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId), company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Enhanced mock data with comprehensive project information
const mockProjects = [
  {
    projectId: 'proj-101',
    projectName: 'Digital Transformation Initiative',
    status: 'On Track',
    priority: 'High',
    overallProgress: 75,
    currentPhase: 'Phase 3: Implementation',
    lastUpdate: 'System integration testing completed successfully. Moving to UAT phase.',
    updateTimestamp: '2025-09-12T14:30:00Z',
    involvedPartners: ['Global Tech Solutions Ltd', 'Premier Consulting'],
    projectManager: 'Sarah Johnson',
    startDate: '2025-06-01',
    endDate: '2025-12-15',
    budget: 450000,
    spent: 337500,
    team: {
      totalMembers: 12,
      activeMembers: 10,
      roles: ['Developers', 'Designers', 'QA Engineers', 'Project Managers']
    },
    milestones: [
      { name: 'Requirements Gathering', status: 'Completed', dueDate: '2025-07-01' },
      { name: 'System Design', status: 'Completed', dueDate: '2025-08-15' },
      { name: 'Development Phase 1', status: 'Completed', dueDate: '2025-09-30' },
      { name: 'Integration Testing', status: 'In Progress', dueDate: '2025-10-15' },
      { name: 'User Acceptance Testing', status: 'Upcoming', dueDate: '2025-11-15' },
      { name: 'Go Live', status: 'Upcoming', dueDate: '2025-12-15' }
    ],
    risks: [
      { level: 'Medium', description: 'Third-party API integration delays' },
      { level: 'Low', description: 'Resource availability during holiday season' }
    ],
    category: 'Technology',
    client: 'Global Tech Solutions Ltd',
  },
  {
    projectId: 'proj-102',
    projectName: 'Manufacturing Process Optimization',
    status: 'Delayed',
    priority: 'High',
    overallProgress: 40,
    currentPhase: 'Phase 2: Analysis',
    lastUpdate: 'Waiting on equipment delivery from supplier. Expected delay of 2 weeks.',
    updateTimestamp: '2025-09-10T09:15:00Z',
    involvedPartners: ['Innovate Manufacturing Corp', 'Advanced Materials Supply Co'],
    projectManager: 'Michael Brown',
    startDate: '2025-07-15',
    endDate: '2025-11-30',
    budget: 280000,
    spent: 112000,
    team: {
      totalMembers: 8,
      activeMembers: 6,
      roles: ['Engineers', 'Analysts', 'Technicians']
    },
    milestones: [
      { name: 'Current State Analysis', status: 'Completed', dueDate: '2025-08-15' },
      { name: 'Process Mapping', status: 'In Progress', dueDate: '2025-09-30' },
      { name: 'Equipment Installation', status: 'Delayed', dueDate: '2025-10-15' },
      { name: 'Testing & Validation', status: 'Upcoming', dueDate: '2025-11-15' },
      { name: 'Process Implementation', status: 'Upcoming', dueDate: '2025-11-30' }
    ],
    risks: [
      { level: 'High', description: 'Equipment delivery delays' },
      { level: 'Medium', description: 'Staff training requirements' }
    ],
    category: 'Manufacturing',
    client: 'Innovate Manufacturing Corp',
  },
  {
    projectId: 'proj-103',
    projectName: 'Financial System Integration',
    status: 'Completed',
    priority: 'Medium',
    overallProgress: 100,
    currentPhase: 'Project Closed',
    lastUpdate: 'Project successfully completed and signed off by client.',
    updateTimestamp: '2025-09-05T16:45:00Z',
    involvedPartners: ['FinTech Solutions Ltd'],
    projectManager: 'Emma Davis',
    startDate: '2025-03-01',
    endDate: '2025-08-31',
    budget: 180000,
    spent: 175000,
    team: {
      totalMembers: 6,
      activeMembers: 0,
      roles: ['Developers', 'Financial Analysts', 'QA Engineers']
    },
    milestones: [
      { name: 'System Analysis', status: 'Completed', dueDate: '2025-04-01' },
      { name: 'Integration Development', status: 'Completed', dueDate: '2025-06-15' },
      { name: 'Testing Phase', status: 'Completed', dueDate: '2025-07-31' },
      { name: 'Go Live', status: 'Completed', dueDate: '2025-08-31' }
    ],
    risks: [],
    category: 'Finance',
    client: 'FinTech Solutions Ltd',
  },
  {
    projectId: 'proj-104',
    projectName: 'Supply Chain Automation',
    status: 'Planning',
    priority: 'Medium',
    overallProgress: 15,
    currentPhase: 'Phase 1: Planning',
    lastUpdate: 'Stakeholder meetings completed. Requirements gathering in progress.',
    updateTimestamp: '2025-09-11T11:20:00Z',
    involvedPartners: ['Logistics Pro Ltd', 'Advanced Materials Supply Co'],
    projectManager: 'James Wilson',
    startDate: '2025-09-01',
    endDate: '2026-03-31',
    budget: 320000,
    spent: 48000,
    team: {
      totalMembers: 10,
      activeMembers: 4,
      roles: ['Business Analysts', 'Developers', 'Supply Chain Experts']
    },
    milestones: [
      { name: 'Requirements Gathering', status: 'In Progress', dueDate: '2025-10-15' },
      { name: 'System Design', status: 'Upcoming', dueDate: '2025-11-30' },
      { name: 'Development Phase', status: 'Upcoming', dueDate: '2026-02-28' },
      { name: 'Implementation', status: 'Upcoming', dueDate: '2026-03-31' }
    ],
    risks: [
      { level: 'Medium', description: 'Complex integration requirements' },
      { level: 'Low', description: 'Stakeholder alignment challenges' }
    ],
    category: 'Logistics',
    client: 'Logistics Pro Ltd',
  },
];

const ProjectsScreen = ({ navigation }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [refreshing, setRefreshing] = useState(false);
  const [projects, setProjects] = useState(mockProjects);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [companyLogo, setCompanyLogo] = useState(null);

  const filters = ['All', 'On Track', 'Delayed', 'Completed', 'Planning'];

  // Load company logo on mount
  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const matchesFilter = activeFilter === 'All' || project.status === activeFilter;
      const matchesSearch = String(project.projectName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(project.category ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(project.client ?? '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [searchQuery, activeFilter, projects]);

  const onRefresh = () => {
    setRefreshing(true);
    // Simulate data refresh
    setTimeout(() => setRefreshing(false), 1000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Track': return COLORS.success;
      case 'Delayed': return COLORS.danger;
      case 'Completed': return COLORS.gray;
      case 'Planning': return COLORS.warning;
      default: return COLORS.gray;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return COLORS.danger;
      case 'Medium': return COLORS.warning;
      case 'Low': return COLORS.success;
      default: return COLORS.gray;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const renderProjectCard = ({ item }) => (
    <TouchableOpacity
      style={styles.projectCard}
      onPress={() => navigation.navigate('ProjectDetails', { project: item })}
    >
      <View style={styles.cardHeader}>
        <View style={styles.projectInfo}>
          <Text style={styles.projectName}>{item.projectName}</Text>
          <Text style={styles.projectClient}>{item.client}</Text>
        </View>
        <View style={styles.statusContainer}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
            <Text style={styles.statusText}>{item.status}</Text>
          </View>
          <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor(item.priority) }]}>
            <Text style={styles.priorityText}>{item.priority}</Text>
          </View>
        </View>
      </View>

      <View style={styles.cardContent}>
        <View style={styles.progressSection}>
          <View style={styles.progressHeader}>
            <Text style={styles.progressLabel}>Progress: {item.overallProgress}%</Text>
            <Text style={styles.currentPhase}>{item.currentPhase}</Text>
          </View>
          <View style={styles.progressBarBackground}>
            <View 
              style={[
                styles.progressBar, 
                { 
                  width: `${item.overallProgress}%`,
                  backgroundColor: getStatusColor(item.status)
                }
              ]} 
            />
          </View>
        </View>

        <View style={styles.projectMetrics}>
          <View style={styles.metric}>
            <MaterialCommunityIcons name="account-group" size={16} color={COLORS.primary} />
            <Text style={styles.metricValue}>{item.team.activeMembers}/{item.team.totalMembers}</Text>
            <Text style={styles.metricLabel}>Team</Text>
          </View>
          <View style={styles.metric}>
            <MaterialCommunityIcons name="calendar-clock" size={16} color={COLORS.warning} />
            <Text style={styles.metricValue}>{formatDate(item.endDate)}</Text>
            <Text style={styles.metricLabel}>Due Date</Text>
          </View>
          <View style={styles.metric}>
            <MaterialCommunityIcons name="currency-gbp" size={16} color={COLORS.success} />
            <Text style={styles.metricValue}>{formatCurrency(item.spent)}</Text>
            <Text style={styles.metricLabel}>Spent</Text>
          </View>
        </View>

        <View style={styles.cardFooter}>
          <Text style={styles.lastUpdate} numberOfLines={2}>
            {item.lastUpdate}
          </Text>
          <View style={styles.actionButtons}>
            <TouchableOpacity style={styles.actionButton}>
              <MaterialCommunityIcons name="chart-line" size={16} color={COLORS.primary} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.actionButton}>
              <MaterialCommunityIcons name="message-text" size={16} color={COLORS.primary} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.actionButton}>
              <MaterialCommunityIcons name="dots-horizontal" size={16} color={COLORS.gray} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderHeader = () => (
    <View style={styles.headerContainer}>
      <View style={styles.titleRow}>
        <View style={styles.titleLeft}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
          ) : (
            <MaterialCommunityIcons name="office-building" size={30} color={COLORS.primary} />
          )}
          <Text style={styles.headerTitle}>Projects</Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity 
            style={styles.viewModeButton}
            onPress={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
          >
            <MaterialCommunityIcons 
              name={viewMode === 'list' ? 'view-grid' : 'view-list'} 
              size={20} 
              color={COLORS.gray} 
            />
          </TouchableOpacity>
          <TouchableOpacity style={styles.addButton}>
            <Ionicons name="add" size={24} color={COLORS.white} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons name="search-outline" size={20} color={COLORS.gray} style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search projects..."
          placeholderTextColor={COLORS.gray}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>

      {/* Filter Buttons */}
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        style={styles.filterScrollView}
        contentContainerStyle={styles.filterContainer}
      >
        {filters.map(filter => (
          <TouchableOpacity
            key={filter}
            style={[styles.filterButton, activeFilter === filter && styles.activeFilter]}
            onPress={() => setActiveFilter(filter)}
          >
            <Text style={[styles.filterText, activeFilter === filter && styles.activeFilterText]}>
              {filter}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Summary Stats */}
      <View style={styles.summaryContainer}>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryNumber}>{projects.length}</Text>
          <Text style={styles.summaryLabel}>Total Projects</Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryNumber}>{projects.filter(p => p.status === 'On Track').length}</Text>
          <Text style={styles.summaryLabel}>On Track</Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryNumber}>{projects.filter(p => p.status === 'Delayed').length}</Text>
          <Text style={styles.summaryLabel}>Delayed</Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryNumber}>{projects.filter(p => p.status === 'Completed').length}</Text>
          <Text style={styles.summaryLabel}>Completed</Text>
        </View>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      <FlatList
        data={filteredProjects}
        renderItem={renderProjectCard}
        keyExtractor={item => item.projectId}
        ListHeaderComponent={renderHeader}
        contentContainerStyle={styles.listContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="briefcase-outline" size={64} color={COLORS.gray} />
            <Text style={styles.emptyText}>No projects found</Text>
            <Text style={styles.emptySubtext}>Try adjusting your search or filters</Text>
          </View>
        }
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.lightGray,
  },
  headerContainer: {
    paddingHorizontal: SIZES.padding,
    paddingTop: SIZES.padding,
    marginBottom: SIZES.base,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SIZES.base * 1.5,
  },
  titleLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    marginRight: SIZES.base,
    resizeMode: 'cover',
  },
  headerTitle: {
    ...FONTS.h1,
    color: COLORS.darkGray,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  viewModeButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: COLORS.white,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: SIZES.base,
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: COLORS.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    borderRadius: SIZES.radius,
    paddingHorizontal: SIZES.base * 1.5,
    marginBottom: SIZES.base * 1.5,
  },
  searchIcon: {
    marginRight: SIZES.base,
  },
  searchInput: {
    flex: 1,
    height: 45,
    ...FONTS.body3,
    color: COLORS.darkGray,
  },
  filterScrollView: {
    marginBottom: SIZES.base * 1.5,
  },
  filterContainer: {
    paddingRight: SIZES.padding,
  },
  filterButton: {
    paddingVertical: SIZES.base,
    paddingHorizontal: SIZES.base * 1.5,
    borderRadius: SIZES.radius * 2,
    backgroundColor: COLORS.white,
    marginRight: SIZES.base,
  },
  activeFilter: {
    backgroundColor: COLORS.primary,
  },
  filterText: {
    ...FONTS.body4,
    color: COLORS.gray,
    fontWeight: '500',
  },
  activeFilterText: {
    color: COLORS.white,
  },
  summaryContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: SIZES.base,
  },
  summaryCard: {
    backgroundColor: COLORS.white,
    borderRadius: SIZES.radius,
    padding: SIZES.base * 1.5,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: SIZES.base / 2,
  },
  summaryNumber: {
    ...FONTS.h2,
    color: COLORS.primary,
    fontWeight: 'bold',
  },
  summaryLabel: {
    ...FONTS.body4,
    color: COLORS.gray,
    marginTop: SIZES.base / 2,
  },
  listContainer: {
    paddingBottom: 80,
  },
  projectCard: {
    backgroundColor: COLORS.white,
    borderRadius: SIZES.radius,
    padding: SIZES.base * 2,
    marginHorizontal: SIZES.padding,
    marginBottom: SIZES.base * 1.5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: SIZES.base * 1.5,
  },
  projectInfo: {
    flex: 1,
    marginRight: SIZES.base,
  },
  projectName: {
    ...FONTS.h3,
    fontWeight: '600',
    color: COLORS.darkGray,
    marginBottom: SIZES.base / 2,
  },
  projectClient: {
    ...FONTS.body4,
    color: COLORS.gray,
  },
  statusContainer: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: SIZES.base,
    paddingVertical: SIZES.base / 2,
    borderRadius: SIZES.radius,
    marginBottom: SIZES.base / 2,
  },
  statusText: {
    ...FONTS.body4,
    color: COLORS.white,
    fontSize: 12,
    fontWeight: 'bold',
  },
  priorityBadge: {
    paddingHorizontal: SIZES.base,
    paddingVertical: SIZES.base / 2,
    borderRadius: SIZES.radius,
  },
  priorityText: {
    ...FONTS.body4,
    color: COLORS.white,
    fontSize: 10,
    fontWeight: 'bold',
  },
  cardContent: {
    marginTop: SIZES.base,
  },
  progressSection: {
    marginBottom: SIZES.base * 1.5,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SIZES.base / 2,
  },
  progressLabel: {
    ...FONTS.body4,
    color: COLORS.darkGray,
    fontWeight: '600',
  },
  currentPhase: {
    ...FONTS.body4,
    color: COLORS.gray,
    fontSize: 12,
  },
  progressBarBackground: {
    height: 6,
    backgroundColor: COLORS.lightGray,
    borderRadius: 3,
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
  },
  projectMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: SIZES.base * 1.5,
  },
  metric: {
    alignItems: 'center',
    flex: 1,
  },
  metricValue: {
    ...FONTS.body4,
    color: COLORS.darkGray,
    fontWeight: '600',
    marginTop: SIZES.base / 2,
  },
  metricLabel: {
    ...FONTS.body4,
    color: COLORS.gray,
    fontSize: 12,
    marginTop: SIZES.base / 4,
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  lastUpdate: {
    ...FONTS.body4,
    color: COLORS.gray,
    flex: 1,
    marginRight: SIZES.base,
    fontStyle: 'italic',
  },
  actionButtons: {
    flexDirection: 'row',
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: COLORS.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: SIZES.base / 2,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 100,
    paddingHorizontal: SIZES.padding,
  },
  emptyText: {
    ...FONTS.h3,
    color: COLORS.gray,
    marginTop: SIZES.base,
  },
  emptySubtext: {
    ...FONTS.body4,
    color: COLORS.gray,
    marginTop: SIZES.base / 2,
    textAlign: 'center',
  },
});

export default ProjectsScreen;